import { UcoastEl } from '@/scripts/core/UcoastEl'

export class PaintSurface extends UcoastEl {
	static htmlSelector = 'paint-surface'
	private svgEl?: SVGSVGElement
	private isInitialized: boolean = false
	private currentSvgSrc?: string
	private resizeTimeout?: number
	private selectedColor: string
	private selectedEmoji: string | null
	private isPaintMode: boolean
	private isEraseMode: boolean
	private isBrushMode: boolean
	private history: string[]
	private historyIndex: number
	private readonly MAX_HISTORY: number
	private originalColors: WeakMap<Element, string>
	private fetchAbort?: AbortController
	private svgObserver?: MutationObserver
	private log(...args: unknown[]) {
		// Filter logs in DevTools by typing: paint-canvas
		console.debug('[paint-canvas][surface]', ...args)
	}

	constructor() {
		super()
		this.selectedColor = '#000000'
		this.selectedEmoji = null
		this.isPaintMode = true
		this.isEraseMode = false
		this.isBrushMode = false
		this.history = []
		this.historyIndex = -1
		this.MAX_HISTORY = 50
		this.originalColors = new WeakMap()
	}

	override connectedCallback() {
		super.connectedCallback()
		this.log('connected')
		// Attempt to fetch and inline SVG (desktop/mobile) if sources are provided
		void this.updateSvgForViewport()
		this.setupOrObserveSvg()
		// respond to viewport changes
		window.addEventListener('resize', this.onWindowResize, { passive: true })
	}

	// Public API consumed by PaintCanvas
	setColor(hex: string) {
		this.selectedColor = hex
		this.log('setColor', hex)
	}

	selectPaintTool() {
		this.isPaintMode = true
		this.isEraseMode = false
		this.isBrushMode = false
		this.selectedEmoji = null
		this.log('selectPaintTool')
		this.updateCursor()
		this.setBrushInteractionMode(false)
	}

	selectBrushTool() {
		this.isBrushMode = true
		this.isPaintMode = false
		this.isEraseMode = false
		this.selectedEmoji = null
		this.log('selectBrushTool')
		this.updateCursor()
		this.setBrushInteractionMode(true)
	}

	// Dragging logic removed

	selectEraseTool() {
		this.isPaintMode = false
		this.isEraseMode = true
		this.isBrushMode = false
		this.selectedEmoji = null
		this.log('selectEraseTool')
		this.updateCursor()
		this.setBrushInteractionMode(false)
	}

	selectMoveTool() {
		// Move tool removed; route to paint tool for compatibility
		this.selectPaintTool()
	}

	selectEmoji(emoji: string | null) {
		this.selectedEmoji = emoji
		this.isPaintMode = false
		this.isEraseMode = false
		this.isBrushMode = false
		this.updateCursor()
		this.setBrushInteractionMode(false)
	}

	canUndo() {
		return this.historyIndex > 0
	}

	canRedo() {
		return this.historyIndex >= 0 && this.historyIndex < this.history.length - 1
	}

	undo() {
		if (!this.svgEl) return
		if (!this.canUndo()) return
		this.restoreState(this.historyIndex - 1)
	}

	redo() {
		if (!this.svgEl) return
		if (!this.canRedo()) return
		this.restoreState(this.historyIndex + 1)
	}

	reset() {
		if (!this.svgEl) return
		this.saveState()
		const groups = Array.from(
			this.svgEl.querySelectorAll<SVGGElement>('[id^="Fill_In_Shapes"]')
		)
		if (!groups.length) return
		groups.forEach((group) => {
			const shapes = group.querySelectorAll<SVGElement>(
				'path, polygon, rect, circle, ellipse'
			)
			shapes.forEach((shape) => {
				const originalFill = this.originalColors.get(shape) ?? 'transparent'
				shape.setAttribute('fill', originalFill)
				this.setInlineStyle(shape, 'fill', originalFill)
				shape.setAttribute('stroke', '#000')
			})
		})
		const emojis = this.svgEl.querySelectorAll('.emoji-sticker')
		emojis.forEach((el) => el.remove())
		// Remove user drawings
		const drawLayer = this.svgEl.querySelector('g#User_Drawings')
		if (drawLayer) drawLayer.remove()
	}

	async saveAsImage() {
		if (!this.svgEl) return
		// Clone SVG to ensure explicit sizing and serialization safety
		const clone = this.svgEl.cloneNode(true) as SVGSVGElement
		const viewBox = this.svgEl.viewBox.baseVal
		if (viewBox && viewBox.width && viewBox.height) {
			clone.setAttribute('width', `${viewBox.width}`)
			clone.setAttribute('height', `${viewBox.height}`)
		}
		clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
		const svgData = new XMLSerializer().serializeToString(clone)
		const svgBlob = new Blob([svgData], {
			type: 'image/svg+xml;charset=utf-8',
		})
		const url = URL.createObjectURL(svgBlob)
		const img = new Image()
		await new Promise<void>((resolve, reject) => {
			img.onload = () => resolve()
			img.onerror = () => reject(new Error('Failed to load SVG image'))
			img.src = url
		})
		const canvas = document.createElement('canvas')
		const ctx = canvas.getContext('2d')
		if (!ctx) return
		const vb = this.svgEl.viewBox.baseVal
		let width = vb?.width || this.svgEl.clientWidth || 1024
		let height = vb?.height || this.svgEl.clientHeight || 1024
		
		// Limit dimensions to prevent memory issues on mobile/low-RAM devices
		const maxDim = 4096
		const scale = Math.min(1, maxDim / Math.max(width, height))
		width = Math.round(width * scale)
		height = Math.round(height * scale)
		
		canvas.width = width
		canvas.height = height
		ctx.fillStyle = '#ffffff'
		ctx.fillRect(0, 0, width, height)
		
		if (scale !== 1) {
			ctx.scale(scale, scale)
		}
		
		ctx.drawImage(img, 0, 0)
		
		// Cross-browser export compatibility with fallback
		const blob = await new Promise<Blob | null>((resolve) => {
			if (canvas.toBlob) {
				canvas.toBlob(resolve, 'image/png')
			} else {
				// Fallback for browsers without toBlob support
				try {
					const dataUrl = canvas.toDataURL('image/png')
					const arr = dataUrl.split(',')
					const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png'
					const bstr = atob(arr[1])
					const n = bstr.length
					const u8arr = new Uint8Array(n)
					for (let i = 0; i < n; i++) {
						u8arr[i] = bstr.charCodeAt(i)
					}
					resolve(new Blob([u8arr], { type: mime }))
				} catch (error) {
					this.log('export: fallback failed', error)
					resolve(null)
				}
			}
		})
		
		if (!blob) {
			this.log('export: failed to create blob')
			URL.revokeObjectURL(url)
			return
		}
		
		const dlUrl = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = dlUrl
		a.download = `paint-set-${Date.now()}.png`
		document.body.appendChild(a)
		a.click()
		document.body.removeChild(a)
		URL.revokeObjectURL(dlUrl)
		URL.revokeObjectURL(url)
	}

	// Internal
	private setupOrObserveSvg() {
		this.svgEl = this.querySelector('svg') as SVGSVGElement | undefined
		if (this.svgEl) {
			this.log('svg: found')
			this.initializeSvg()
			return
		}
		this.log('svg: not found, observing')
		// Observe for late-inserted SVG (e.g., theme editor or ajax)
		this.svgObserver = new MutationObserver(() => {
			this.svgEl = this.querySelector('svg') as SVGSVGElement | undefined
			if (this.svgEl) {
				this.log('svg: discovered via MutationObserver')
				this.initializeSvg()
				this.svgObserver?.disconnect()
			}
		})
		this.svgObserver.observe(this, { childList: true, subtree: true })
	}

	private initializeSvg() {
		if (!this.svgEl) return
		if (this.isInitialized) return
		this.log('initializeSvg')
		this.applyResponsiveSvgAttributes()
		this.centerTopGroup()
		this.processShapes(this.svgEl)
		this.saveState() // initial state
		// Set accessibility attributes
		this.svgEl.setAttribute('role', 'img')
		const ariaLabel = this.getAttribute('data-aria-label') || 'Interactive coloring canvas'
		this.svgEl.setAttribute('aria-label', ariaLabel)
		// Single click handler at svg root
		this.svgEl.addEventListener('click', this.onSvgClick.bind(this))
		// Brush drawing handler
		this.svgEl.addEventListener('pointerdown', this.onBrushPointerDown, { passive: false })
		this.updateCursor()
		// Ensure interaction mode reflects current tool on init
		this.setBrushInteractionMode(this.isBrushMode)
		this.isInitialized = true
	}

	// Public: reflow and ensure correct source, sizing, padding, centering
	public async reflow(): Promise<void> {
		await this.updateSvgForViewport()
		this.applyResponsiveSvgAttributes()
		this.centerTopGroup()
		this.applyMaxHeightConstraint()
	}

	// Choose the correct source based on viewport and load if needed
	private async updateSvgForViewport(): Promise<void> {
		try {
			// Abort any in-flight fetch
			this.fetchAbort?.abort()
			this.fetchAbort = new AbortController()

			const desktopSrc = this.getAttribute('data-svg-src') || undefined
			const mobileSrc = this.getAttribute('data-svg-src-mobile') || undefined
			if (!desktopSrc && !mobileSrc) return
			const isMobile = window.innerWidth < 750
			const src = isMobile && mobileSrc ? mobileSrc : (desktopSrc || mobileSrc)
			if (!src) return
			if (this.currentSvgSrc === src && this.svgEl) {
				// still ensure centering in case container changed
				this.centerTopGroup()
				return
			}
			this.log('svg: fetching from', src)
			
			// Safari mobile compatibility: use no-cors mode as fallback, handle errors gracefully
			let res: Response
			try {
				res = await fetch(src, { mode: 'cors', credentials: 'omit', signal: this.fetchAbort.signal })
			} catch (fetchError) {
				// Safari mobile may fail on CORS, try no-cors as fallback
				this.log('svg: cors fetch failed, trying no-cors', fetchError)
				try {
					res = await fetch(src, { mode: 'no-cors', signal: this.fetchAbort.signal })
				} catch (fallbackError) {
					this.log('svg: fetch failed completely', fallbackError)
					return
				}
			}
			
			if (!res.ok && res.type !== 'opaque') {
				this.log('svg: fetch failed', res.status, res.statusText)
				return
			}
			
			let text: string
			try {
				text = await res.text()
			} catch (textError) {
				this.log('svg: failed to read response text', textError)
				return
			}
			
			if (!text || text.trim().length === 0) {
				this.log('svg: empty response')
				return
			}
			
			const parser = new DOMParser()
			let doc: Document
			try {
				doc = parser.parseFromString(text, 'image/svg+xml')
			} catch (parseError) {
				this.log('svg: DOMParser failed', parseError)
				return
			}
			
			// Check for DOMParser errors (Safari may return error elements)
			const parserError = doc.querySelector('parsererror')
			if (parserError) {
				this.log('svg: DOMParser reported error', parserError.textContent)
				return
			}
			
			// Prefer the root element if it is svg, otherwise find one
			let fetchedSvg: SVGSVGElement | null = null
			if (doc.documentElement?.nodeName.toLowerCase() === 'svg') {
				fetchedSvg = doc.documentElement as unknown as SVGSVGElement
			} else {
				fetchedSvg = doc.querySelector<SVGSVGElement>('svg')
			}
			
			if (!fetchedSvg || fetchedSvg.nodeName.toLowerCase() !== 'svg') {
				this.log('svg: fetched content did not contain an <svg> root')
				return
			}
			
			// Import into this document to preserve eventing compatibility
			let svg: SVGSVGElement
			try {
				svg = document.importNode(fetchedSvg, true) as SVGSVGElement
			} catch (importError) {
				this.log('svg: importNode failed', importError)
				return
			}
			
			// Sanitize SVG before insertion
			this.sanitizeSvg(svg)
			
			// Replace any existing content - wrap in try-catch for Safari mobile
			try {
				this.innerHTML = ''
				this.appendChild(svg)
			} catch (domError) {
				this.log('svg: DOM manipulation failed', domError)
				return
			}
			
			this.isInitialized = false
			this.svgEl = svg
			// Initialize immediately
			this.initializeSvg()
			this.currentSvgSrc = src
			this.log('svg: inlined from url')
		} catch (e) {
			// Catch-all for any unexpected errors
			this.log('svg: unexpected error', e)
			// Ensure we don't break page rendering
			if (this.svgEl) {
				// If we have an existing SVG, keep it
				return
			}
			// Otherwise, log but don't throw
			console.error('[paint-surface] Failed to load SVG:', e)
		}
	}

	private onWindowResize = () => {
		if (this.resizeTimeout) window.clearTimeout(this.resizeTimeout)
		this.resizeTimeout = window.setTimeout(() => {
			void this.updateSvgForViewport()
			// re-center in case container size changed
			this.applyResponsiveSvgAttributes()
			this.centerTopGroup()
			// re-apply height constraints after any layout change
			this.applyMaxHeightConstraint()
		}, 150)
	}

	private applyResponsiveSvgAttributes() {
		if (!this.svgEl) return
		// ensure viewBox
		if (!this.svgEl.viewBox?.baseVal?.width || this.svgEl.viewBox.baseVal.width === 0) {
			try {
				const bbox = this.svgEl.getBBox()
				if (bbox && bbox.width && bbox.height) {
					this.svgEl.setAttribute('viewBox', `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`)
				} else {
					const width = parseFloat(this.svgEl.getAttribute('width') || '0') || 1024
					const height = parseFloat(this.svgEl.getAttribute('height') || '0') || 1024
					this.svgEl.setAttribute('viewBox', `0 0 ${width} ${height}`)
				}
			} catch {
				const width = parseFloat(this.svgEl.getAttribute('width') || '0') || 1024
				const height = parseFloat(this.svgEl.getAttribute('height') || '0') || 1024
				this.svgEl.setAttribute('viewBox', `0 0 ${width} ${height}`)
			}
		}
		this.svgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet')
		this.svgEl.style.width = '100%'
		this.svgEl.style.height = '100%'
		this.svgEl.style.display = 'block'
		// cap vertical size to avoid overly tall rendering
		this.applyMaxHeightConstraint()
		// expand viewBox to include padding if requested
		this.applyPaddedViewBox()
	}

	private centerTopGroup() {
		if (!this.svgEl) return
		// find top-most <g> child
		const children = Array.from(this.svgEl.children) as Element[]
		const topGroup = children.find((c) => c.tagName.toLowerCase() === 'g') as SVGGElement | undefined
		if (!topGroup) return
		// ensure base transform is stored
		const baseTransform = (topGroup as any).dataset?.baseTransform ?? topGroup.getAttribute('transform') ?? ''
		;(topGroup as any).dataset.baseTransform = baseTransform
		// compute centers
		let vb = this.svgEl.viewBox?.baseVal
		if (!vb || !vb.width || !vb.height) {
			this.applyResponsiveSvgAttributes()
			vb = this.svgEl.viewBox?.baseVal
			if (!vb || !vb.width || !vb.height) return
		}
		let bbox: DOMRect
		try {
			bbox = topGroup.getBBox()
		} catch {
			return
		}
		const viewCenterX = vb.x + vb.width / 2
		const viewCenterY = vb.y + vb.height / 2
		const groupCenterX = bbox.x + bbox.width / 2
		const groupCenterY = bbox.y + bbox.height / 2
		const dx = viewCenterX - groupCenterX
		const dy = viewCenterY - groupCenterY
		const transform = `${baseTransform} translate(${dx.toFixed(2)} ${dy.toFixed(2)})`.trim()
		topGroup.setAttribute('transform', transform)
	}

	override disconnectedCallback() {
		super.disconnectedCallback?.()
		this.fetchAbort?.abort()
		this.svgObserver?.disconnect()
		window.removeEventListener('resize', this.onWindowResize)
		// Restore default interaction behavior
		this.setBrushInteractionMode(false)
	}

	private getPaddingConfig(): { type: 'percent' | 'absolute'; value: number } {
		const raw = (this.getAttribute('data-svg-padding') || '').trim()
		if (!raw) return { type: 'percent', value: 5 } // default 5%
		if (raw.endsWith('%')) {
			const v = parseFloat(raw.slice(0, -1))
			if (!Number.isFinite(v) || v < 0) return { type: 'percent', value: 5 }
			return { type: 'percent', value: v }
		}
		const v = parseFloat(raw)
		if (!Number.isFinite(v) || v < 0) return { type: 'percent', value: 5 }
		return { type: 'absolute', value: v }
	}

	private applyPaddedViewBox() {
		if (!this.svgEl) return
		let bbox: DOMRect
		try {
			// getBBox on SVG returns the tight bounds of its rendered content
			bbox = this.svgEl.getBBox()
		} catch {
			return
		}
		if (!bbox?.width || !bbox?.height) return
		const padCfg = this.getPaddingConfig()
		const padX = padCfg.type === 'percent' ? (bbox.width * padCfg.value) / 100 : padCfg.value
		// Double vertical padding on mobile only
		const isMobile = window.innerWidth < 750
		const basePadY = padCfg.type === 'percent' ? (bbox.height * padCfg.value) / 100 : padCfg.value
		const padYTop = isMobile ? basePadY * 4 : basePadY
		const padYBottom = isMobile ? basePadY * 2.5 : basePadY
		const x = bbox.x - padX
		const y = bbox.y - padYTop
		const w = bbox.width + padX * 2
		const h = bbox.height + padYTop + padYBottom
		this.svgEl.setAttribute('viewBox', `${x} ${y} ${w} ${h}`)
	}

	private getMaxHeightCss(): string {
		const raw = (this.getAttribute('data-svg-max-height') || '').trim()
		// default
		if (!raw) return '90vh'
		// allow raw CSS length values
		if (/(vh|vw|px|rem|em|%)$/.test(raw)) return raw
		// numeric -> px
		const num = parseFloat(raw)
		if (Number.isFinite(num)) return `${num}px`
		return '90vh'
	}

	private applyMaxHeightConstraint() {
		if (!this.svgEl) return
		const configured = this.getMaxHeightCss()
		// On mobile, subtract the footer height if present
		const isMobile = window.innerWidth < 750
		let footerHeight = 0
		let toolsHeight = 0
		if (isMobile) {
			const parent = this.closest('paint-canvas') as HTMLElement | null
			const footer = parent?.querySelector('.paint-canvas-footer') as HTMLElement | null
			const tools = parent?.querySelector('.paint-canvas-tools') as HTMLElement | null
			const rect = footer?.getBoundingClientRect()
			const toolsRect = tools?.getBoundingClientRect()
			if (rect && rect.height) footerHeight = Math.max(0, Math.round(rect.height))
			if (toolsRect && toolsRect.height) toolsHeight = Math.max(0, Math.round(toolsRect.height))
		}
		const totalSubtract = footerHeight + toolsHeight
		if (isMobile && totalSubtract > 0) {
			// Use CSS min() to ensure we respect the configured max and also the viewport minus footer
			this.svgEl.style.maxHeight = `min(${configured}, calc(100vh - ${totalSubtract}px))`
		} else {
			this.svgEl.style.maxHeight = configured
		}
	}
	private onSvgClick(event: MouseEvent) {
		if (!this.svgEl) return
		event.stopPropagation()
		if (this.isBrushMode) return
		// Move tool removed

		// Emoji placement mode
		if (this.selectedEmoji) {
			this.log('click: place emoji', this.selectedEmoji)
			const { x, y } = this.getSVGCoordinates(this.svgEl, event)
			const text = document.createElementNS(
				'http://www.w3.org/2000/svg',
				'text'
			)
			text.setAttribute('x', `${x}`)
			text.setAttribute('y', `${y}`)
			text.setAttribute('font-size', '40')
			text.setAttribute('text-anchor', 'middle')
			text.setAttribute('dominant-baseline', 'middle')
			text.setAttribute('class', 'emoji-sticker')
			// Allow dragging later; prevent selection only
			text.setAttribute('style', 'user-select: none; -webkit-user-select: none;')
			text.textContent = this.selectedEmoji
			// animate grow-in to final size from the cursor position (respect reduced motion)
			const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
			;(text as any).dataset.scale = '1'
			;(text as any).dataset.tx = '0'
			;(text as any).dataset.ty = '0'
			// Use view-box coordinates so origin matches SVG user space
			;(text.style as any).transformBox = 'view-box'
			text.style.transformOrigin = `${x}px ${y}px`
			if (!prefersReducedMotion) {
				text.style.transform = 'scale(0.3)'
				text.style.transition = 'transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1)'
				this.svgEl.appendChild(text)
				requestAnimationFrame(() => {
					text.style.transform = 'scale(1)'
				})
			} else {
				this.svgEl.appendChild(text)
			}
			// Snapshot state after DOM is updated for better perceived performance
			this.saveState()
			return
		}

		// Paint / erase mode
		if (!this.isPaintMode && !this.isEraseMode) return

		// If erasing and clicked on an emoji, remove it
		if (this.isEraseMode) {
			const target = event.target as HTMLElement
			const emoji = target.closest?.('.emoji-sticker') as SVGElement | null
			if (emoji) {
				this.log('erase: emoji')
				emoji.remove()
				// Snapshot after removal
				this.saveState()
				return
			}
			// If erasing and clicked on a brush stroke, remove it
			const maybePath = (target as Element).closest?.('path') as SVGPathElement | null
			if (maybePath) {
				const inDrawLayer = !!maybePath.closest('#User_Drawings')
				if (maybePath.classList.contains('brush-stroke') || inDrawLayer) {
					this.log('erase: brush stroke')
					maybePath.remove()
					// Snapshot after removal
					this.saveState()
					return
				}
			}
		}

		const fillableShapes = ['polygon', 'rect', 'circle', 'ellipse', 'path']
		let target = event.target as Element | null
		while (target && target !== this.svgEl) {
			if (fillableShapes.includes(target.tagName.toLowerCase())) break
			target = (target as HTMLElement).parentElement
		}
		if (!target || target === this.svgEl) return

		// Verify the target is inside any Fill_In_Shapes group
		const containingFillGroup = (target as Element).closest?.(
			'[id^="Fill_In_Shapes"]'
		) as SVGGElement | null
		if (!containingFillGroup || !this.svgEl.contains(containingFillGroup)) return

		const newFill = this.isEraseMode ? 'transparent' : this.selectedColor
		const currentFill = target.getAttribute('fill') || 'transparent'
		if (currentFill === newFill) return

		this.log('click: paint', {
			mode: this.isEraseMode ? 'erase' : 'paint',
			newFill,
			tag: target.tagName,
		})
		// Apply visual update first for responsiveness
		;(target as SVGElement).setAttribute('fill', newFill)
		;(target as SVGElement).style.setProperty('fill', newFill)
		// only set stroke if shape has no existing stroke or stroke-width
		if (!(target as SVGElement).getAttribute('stroke') && !(target as SVGElement).getAttribute('stroke-width')) {
			;(target as SVGElement).setAttribute('stroke', '#000')
		}
		// Snapshot after changes
		this.saveState()
	}

	// Emoji dragging removed

	private updateCursor() {
		if (!this.svgEl) return
		// Emoji cursor takes precedence
		if (this.selectedEmoji) {
			this.svgEl.style.cursor = this.buildEmojiCursor(this.selectedEmoji)
			return
		}
		// Brush tool
		if (this.isBrushMode) {
			this.svgEl.style.cursor = 'crosshair'
			return
		}
		// Erase tool
		if (this.isEraseMode) {
			this.svgEl.style.cursor = 'cell'
			return
		}
		// Paint tool
		if (this.isPaintMode) {
			this.svgEl.style.cursor = 'crosshair'
			return
		}
		// Fallback
		this.svgEl.style.cursor = ''
	}

	private buildEmojiCursor(emoji: string) {
		// Create a small SVG data URL containing the emoji as text
		const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
  <text x="20" y="28" font-size="28" text-anchor="middle">${emoji}</text>
</svg>`
		const encoded = encodeURIComponent(svg)
		// Hotspot roughly centered at 20,20
		return `url("data:image/svg+xml;utf8,${encoded}") 20 20, auto`
	}

	private processShapes(svg: SVGSVGElement) {
		const groups = Array.from(
			svg.querySelectorAll<SVGGElement>('[id^="Fill_In_Shapes"]')
		)
		if (!groups.length) return
		let total = 0
		groups.forEach((group) => {
			const shapes = group.querySelectorAll<SVGElement>(
				'path, polygon, rect, circle, ellipse'
			)
			total += shapes.length
		})
		
		// Use requestIdleCallback for large SVGs to avoid blocking main thread
		const processBatch = () => {
			groups.forEach((group) => {
				const shapes = group.querySelectorAll<SVGElement>(
					'path, polygon, rect, circle, ellipse'
				)
				shapes.forEach((shape) => {
					// Pointer events to ensure clickability
					shape.style.setProperty('pointer-events', 'all')
					// Record original fill
					const fillAttr = shape.getAttribute('fill') || 'transparent'
					this.originalColors.set(shape, fillAttr)
					// Ensure explicit stroke only if missing
					if (!shape.getAttribute('stroke') && !shape.getAttribute('stroke-width')) {
						shape.setAttribute('stroke', '#000')
					}
					// If CSS sets fill:none, make inline style explicit to allow later overrides
					const currentFill = shape.getAttribute('fill')
					if (!currentFill || currentFill === 'none') {
						shape.setAttribute('fill', 'transparent')
						shape.style.setProperty('fill', 'transparent')
					}
				})
			})
		}
		
		if (total > 100 && window.requestIdleCallback) {
			window.requestIdleCallback(processBatch, { timeout: 1000 })
		} else {
			processBatch()
		}
		
		this.log('processShapes: groups', groups.length, 'total shapes', total)
	}

	private setInlineStyle(el: SVGElement, property: string, value: string) {
		// Fast path: use CSSStyleDeclaration rather than string manipulation
		el.style.setProperty(property, value)
	}

	// Brush drawing
	private onBrushPointerDown = (event: PointerEvent) => {
		if (!this.svgEl) return
		if (!this.isBrushMode) return
		// Prevent page scrolling/refresh gestures on mobile
		event.preventDefault()
		event.stopPropagation()
		// Harden against UA handling with CSS while drawing
		this.setBrushInteractionMode(true)
		// Begin history
		this.saveState()
		// Capture pointer for continuous drawing
		;(this.svgEl as any).setPointerCapture?.(event.pointerId)
		const { x, y } = this.getSVGCoordinates(this.svgEl, event as unknown as MouseEvent)
		// Ensure layer
		const layer = this.getOrCreateDrawLayer()
		// Create path
		const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
		path.setAttribute('fill', 'none')
		path.setAttribute('stroke', this.selectedColor)
		path.setAttribute('stroke-width', this.getBrushWidth().toString())
		path.setAttribute('stroke-linecap', 'round')
		path.setAttribute('stroke-linejoin', 'round')
		path.setAttribute('vector-effect', 'non-scaling-stroke')
		path.setAttribute('class', 'brush-stroke')
		path.setAttribute('d', `M ${x} ${y} L ${x} ${y}`)
		layer.appendChild(path)
		;(this as any)._brushPath = path as SVGPathElement
		;(this as any)._lastBrushPoint = { x, y }
		// Listen for move/end on window to keep drawing even if cursor leaves svg
		window.addEventListener('pointermove', this.onBrushPointerMove, { passive: false })
		window.addEventListener('pointerup', this.onBrushPointerUp)
		window.addEventListener('pointercancel', this.onBrushPointerUp)
	}

	private onBrushPointerMove = (event: PointerEvent) => {
		if (!this.svgEl) return
		if (!this.isBrushMode) return
		const path = (this as any)._brushPath as SVGPathElement | undefined
		if (!path) return
		// prevent page scroll while drawing on touch devices
		event.preventDefault()
		const { x, y } = this.getSVGCoordinates(this.svgEl, event as unknown as MouseEvent)
		const d = path.getAttribute('d') || ''
		path.setAttribute('d', `${d} L ${x} ${y}`)
		;(this as any)._lastBrushPoint = { x, y }
	}

	private onBrushPointerUp = (_event: PointerEvent) => {
		if (!this.svgEl) return
		// cleanup listeners
		window.removeEventListener('pointermove', this.onBrushPointerMove)
		window.removeEventListener('pointerup', this.onBrushPointerUp)
		window.removeEventListener('pointercancel', this.onBrushPointerUp)
		;(this as any)._brushPath = undefined
		;(this as any)._lastBrushPoint = undefined
		// Save state after stroke completes for proper undo granularity
		this.saveState()
		// Re-enable normal interaction mode
		this.setBrushInteractionMode(false)
	}

	private setBrushInteractionMode(enabled: boolean) {
		if (!this.svgEl) return
		// Prevent touch panning/zooming while drawing
		this.svgEl.style.touchAction = enabled ? 'none' : ''
		// Contain overscroll to avoid pull-to-refresh/bounce (use any for Safari versions)
		;(this.svgEl.style as any).overscrollBehavior = enabled ? 'contain' : ''
		const container = this.closest('paint-canvas') as HTMLElement | null
		if (container) {
			;(container.style as any).overscrollBehavior = enabled ? 'contain' : ''
		}
	}

	private getBrushWidth(): number {
		// Allow configuration via attribute on host: data-brush-width="number"
		const raw = (this.getAttribute('data-brush-width') || '').trim()
		const num = parseFloat(raw)
		if (Number.isFinite(num) && num > 0) return num
		return 4
	}

	private getOrCreateDrawLayer(): SVGGElement {
		let layer = this.svgEl!.querySelector('g#User_Drawings') as SVGGElement | null
		if (!layer) {
			layer = document.createElementNS('http://www.w3.org/2000/svg', 'g')
			layer.setAttribute('id', 'User_Drawings')
			// Place on top for visibility
			this.svgEl!.appendChild(layer)
		}
		return layer
	}

	private getSVGCoordinates(svg: SVGSVGElement, event: MouseEvent) {
		// Use the current transformation matrix for precise mapping from screen to SVG coords
		const pt = svg.createSVGPoint()
		pt.x = event.clientX
		pt.y = event.clientY
		const ctm = svg.getScreenCTM()
		if (!ctm) return { x: 0, y: 0 }
		const inv = ctm.inverse()
		const result = pt.matrixTransform(inv)
		return { x: result.x, y: result.y }
	}

	private saveState() {
		if (!this.svgEl) return
		const state = this.svgEl.innerHTML
		// Trim future states if we've undone
		if (this.historyIndex < this.history.length - 1) {
			this.history = this.history.slice(0, this.historyIndex + 1)
		}
		this.history.push(state)
		this.historyIndex++
		this.log('history: save', { index: this.historyIndex, length: this.history.length })
		if (this.history.length > this.MAX_HISTORY) {
			this.history.shift()
			this.historyIndex--
		}
		this.dispatchHistoryChange()
	}

	private restoreState(index: number) {
		if (!this.svgEl) return
		if (index < 0 || index >= this.history.length) return
		this.svgEl.innerHTML = this.history[index]
		this.historyIndex = index
		this.log('history: restore', { index: this.historyIndex, length: this.history.length })
		// Reprocess shapes after DOM replacement
		this.processShapes(this.svgEl)
		// Update cursor after DOM replacement
		this.updateCursor()
		this.dispatchHistoryChange()
	}

	private dispatchHistoryChange() {
		this.dispatchEvent(
			new CustomEvent('paint:historychange', {
				bubbles: true,
				composed: true,
				detail: {
					canUndo: this.canUndo(),
					canRedo: this.canRedo(),
				},
			})
		)
	}

	private sanitizeSvg(svg: SVGSVGElement) {
		// Remove dangerous elements
		const scripts = svg.querySelectorAll('script, foreignObject')
		scripts.forEach((n) => n.remove())

		// Walk tree and remove dangerous attributes
		const walker = document.createTreeWalker(svg, NodeFilter.SHOW_ELEMENT)
		let node = walker.nextNode() as Element | null
		while (node) {
			for (const attr of Array.from(node.attributes)) {
				const name = attr.name.toLowerCase()
				const value = attr.value.trim()
				// Remove inline event handlers
				if (name.startsWith('on')) {
					node.removeAttribute(attr.name)
				}
				// Remove javascript: URLs
				if ((name === 'href' || name === 'xlink:href') && value.toLowerCase().startsWith('javascript:')) {
					node.removeAttribute(attr.name)
				}
			}
			node = walker.nextNode() as Element | null
		}
	}
}


